export const RoleEnum = {
  BETA_ADMINISTRATOR: 'Beta Administrator',
  OWNER: 'Owner',
  BUSINESS_ADMIN: 'Business Admin',
  REPRESENTATIVE: 'Representative',
}

export const PermissionEnum = {
  VIEW_DASHBOARD: 'view_dashboard',
  EDIT_PROFILE: 'edit_profile',
  MANAGE_USERS: 'manage_users',
  ASSIGN_ROLE: 'assign_role',
}

export const RoutesEnum = {
  VERIFY_EMAIL: "/verify-email",
  EXPIRED_VERIFICATION: "/expired-verification",
  LOGIN: "/login",
  CHOOSE_ACCOUNT: "/choose-account",
  REPRESENTATIVE: "/representative",
  SEND_REPRESENTATIVE_EMAIL: "/send-representative-email",
  OWNER: "/owner",
  ERROR: "/error",
  BUSINESS_DASHBOARD: "/business",
  REPRESENTATIVE_DASHBOARD: "/representative",
  ACCESS_CODE: "/access-code",
  RESET_PASSWORD: "/reset-password",
  EXPIRED_RESET_PASSWORD_LINK: "/expired-reset-password-link",
  CLAIM_BUSINESS: "/kyb/claim-business",
  ENTITY_DETAILS: "/kyb/entity-details",
  SETTINGS: "/settings",
  SETTINGS_MANAGE_SUBSCRIPTION: "/settings/billing/manage-subscription",
  SETTINGS_BILLING_RETURN: "/settings/billing/return",
  LOAN_BEACON_LOAN_INFORMATION: '/business/funding-passport/loan-information',
  LOAN_BEACON_PERSONAL_CREDIT_SCORE: '/business/funding-passport/personal-credit-score',
};

export const Tooltips = {
  FINANCIALS_REVENUE_HEADER: "Revenue",
  FINANCIALS_REVENUE_BODY: "Revenue represents the total income a business generates from its core operations, primarily through the sale of goods or services. It is the top line figure on the Income Statement, reflecting the gross income before any expenses are deducted. Understanding revenue is crucial for evaluating a company's sales performance and overall business activity. It can be influenced by factors like sales volume, pricing strategies, and market demand.",
  FINANCIALS_COST_OF_REVENUE_HEADER: "Cost of Revenue or Cost of Goods Sold",
  FINANCIALS_COST_OF_REVENUE_BODY: "COGS encompasses the direct costs directly attributable to the production of the goods a company sells. This includes the cost of materials, direct labor, and manufacturing overhead. It is an essential expense found on the Income Statement, as it directly impacts a company's gross profit. Effectively managing COGS is vital for maintaining profitability. Fluctuations in COGS can be due to changes in raw material prices, production efficiency, or inventory management.",
  FINANCIALS_NET_INCOME_HEADER: "Net Income",
  FINANCIALS_NET_INCOME_BODY: "Net income, often referred to as the bottom line, signifies a company's total earnings or profit after all expenses, including taxes and interest, have been deducted from revenue. It is a critical metric found on the Income Statement that reflects a company's overall financial performance and profitability over a specific period. Net income is a key factor in determining a company's financial health and its ability to distribute dividends or reinvest in the business",
  FINANCIALS_OPERATING_INCOME_HEADER: "Operating Income",
  FINANCIALS_OPERATING_INCOME_BODY: "Operating income measures the profit derived from a business's core operations, before accounting for interest expenses and income taxes. It provides insight into the operational efficiency and profitability of a company's primary business activities. Located on the Income Statement, operating income helps assess how well a company manages its operating expenses. This metric is particularly useful for comparing companies within the same industry, as it eliminates the impact of different financing and tax structures.",
  FINANCIALS_CURRENT_ASSETS_HEADER: "Current Assets",
  FINANCIALS_CURRENT_ASSETS_BODY: "Current assets are resources that a company expects to convert into cash or use up within one year, in the normal course of business. These assets, found on the Balance Sheet, are essential for day-to-day operations and include cash, accounts receivable, inventory, and prepaid expenses. Maintaining a healthy level of current assets is vital for ensuring liquidity and meeting short-term obligations. Monitoring current assets helps assess a company's ability to pay its bills and fund ongoing activities.",
  FINANCIALS_CURRENT_LIABILITIES_HEADER: "Current Liabilities",
  FINANCIALS_CURRENT_LIABILITIES_BODY: "Current liabilities are debts or obligations that a company must settle within one year. These liabilities, found on the Balance Sheet, include accounts payable, short-term loans, and accrued expenses. Managing current liabilities effectively is crucial for maintaining financial stability. These obligations represent the company's immediate financial responsibilities and impact its short-term liquidity. Timely payment of current liabilities is essential to avoid penalties and maintain good relationships with creditors.",
  FINANCIALS_TOTAL_LIABILITIES_HEADER: "Total Liabilities",
  FINANCIALS_TOTAL_LIABILITIES_BODY: "Total liabilities represent the sum of all debts and obligations a company owes to external parties. Located on the Balance Sheet, these liabilities include both current and non-current obligations, such as long-term debt and deferred tax liabilities. Total liabilities reflect a company's overall financial leverage and its reliance on external financing. A high level of total liabilities may indicate increased financial risk, while a low level could suggest financial strength.",
  FINANCIALS_TOTAL_ASSETS_HEADER: "Total Assets",
  FINANCIALS_TOTAL_ASSETS_BODY: "Total assets represent the sum of all resources a company owns, which have economic value. Found on the Balance Sheet, these assets include current assets, non-current assets (like property, plant, and equipment), and intangible assets. Total assets reflect a company's financial resources and its ability to generate income. Understanding total assets provides insights into a company's size, scale, and capacity.",
  FINANCIALS_OTHER_ASSETS_HEADER: "Other Assets",
  FINANCIALS_OTHER_ASSETS_BODY: "Other assets are a category on the Balance Sheet that includes assets not classified as current assets, investments, or fixed assets. This may include long-term prepaid expenses, deferred charges, or other miscellaneous assets. Other assets are often less liquid than current assets and require further analysis to understand their value and impact on the company's financial position.",
  FINANCIALS_TOTAL_PERSONAL_LIABILITIES_HEADER: "Total Personal Liabilities",
  FINANCIALS_TOTAL_PERSONAL_LIABILITIES_BODY: "Total personal liabilities represent the sum of all debts and obligations owed by an individual. This figure, found on a Personal Balance Sheet, includes various forms of debt, such as mortgages, car loans, credit card balances, and other personal loans. Understanding total personal liabilities is essential for managing personal finances effectively. It provides a comprehensive view of an individual's financial obligations and helps assess their debt burden.",
  FINANCIALS_TOTAL_PERSONAL_ASSETS_HEADER: "Total Personal Assets",
  FINANCIALS_TOTAL_PERSONAL_ASSETS_BODY: "Total personal assets represent the sum of all resources owned by an individual, which have economic value. This is also found on a Personal Balance Sheet, including cash, investments, real estate, vehicles, and other personal property. Total personal assets reflect an individual's financial resources and wealth. Tracking total personal assets is crucial for personal financial planning and wealth management.",
  CREDITBOX_DEBT_COVERAGE_HEADER: 'Debt Coverage',
  CREDITBOX_DEBT_COVERAGE_BODY: `The borrower's recurring cash flow after operating expenses compared with the scheduled principal + interest due each period. It answers: "Can you comfortably pay the debt from normal operations?"`,
  CREDITBOX_PROFITABILITY_HEADER: 'Profitability',
  CREDITBOX_PROFITABILITY_BODY: `The borrower's demonstrated ability to earn positive, sustainable profit from its core business activities — evidence that the business model actually produces surplus cash rather than simply circulating it.`,
  CREDITBOX_LIQUIDITY_HEADER: 'Liquidity',
  CREDITBOX_LIQUIDITY_BODY: `The borrower's capacity to meet short-term obligations (≤ 12 months) without selling long-term assets or drawing new debt. In practice, it is the buffer of cash, near-cash, and working-capital assets available on demand.`,
  CREDITBOX_LEVERAGE_HEADER: 'Leverage',
  CREDITBOX_LEVERAGE_BODY: `The extent to which the borrower's assets are financed with debt instead of equity — a measure of balance-sheet risk that magnifies both returns and potential losses.`,
  CREDITBOX_CAPITAL_ADEQUACY_HEADER: 'Capital Adequacy',
  CREDITBOX_CAPITAL_ADEQUACY_BODY: `The depth and quality of the borrower's equity cushion — retained earnings or contributed capital that can absorb unexpected losses before creditors are affected.`,
  CREDITBOX_COLLATERAL_COVERAGE_HEADER: 'Collateral Coverage',
  CREDITBOX_COLLATERAL_COVERAGE_BODY: `The relationship between the liquidation value of pledged collateral and the outstanding loan balance — an estimate of how much the bank could recover if it had to seize and sell the assets.`,
  CREDITBOX_GUARANTOR_STRENGTH_HEADER: 'Guarantor Strength',
  CREDITBOX_GUARANTOR_STRENGTH_BODY: `The financial wherewithal and willingness of any individuals or entities that guarantee the loan — effectively a secondary source of repayment if the primary borrower falters.`,
  TRACKER_HEADER: 'Tracking BetaScores',
  TRACKER_BODY: `Keep track of all of the business profiles viewed by you and your organization. By default, the profiles are listed in order of expiration. When the viewing window of a profile has expired, it is removed from the list. If you wish to view said business again after expiration, you must search for the business again, which counts towards your usage.`,
}

export const BetaScoreTiers = {
  PRIME_TIER: 'Prime',
  GROWTH_TIER: 'Growth',
  EMERGING_TIER: 'Emerging',
  MONITOR_TIER: 'Monitor',
  CAUTION_TIER: 'Caution',
  UNAVAILABLE_TIER: 'BetaScore Unavailable',
};

export const BetaScoreLabels = {
  PRIME_TIER_LABEL: 'This business is ready to scale',
  GROWTH_TIER_LABEL: 'This business has strong profile',
  EMERGING_TIER_LABEL: 'This is a growing business',
  MONITOR_TIER_LABEL: 'This business requires attention',
  CAUTION_TIER_LABEL: 'This business is at risk of failing',
  UNAVAILABLE_TIER_LABEL: 'This business has not yet provided the information to generate a BetaScore.',
};

export const BetaScoreColors = {
  PRIME_TIER_COLOR: 5,
  GROWTH_TIER_COLOR: 4,
  EMERGING_TIER_COLOR: 3,
  MONITOR_TIER_COLOR: 2,
  CAUTION_TIER_COLOR: 1,
};

export const BetaScoreTierMapping = [
  {
    minRawScore: 80,
    tier: BetaScoreTiers.PRIME_TIER,
    label: BetaScoreLabels.PRIME_TIER_LABEL,
    colorBucket: BetaScoreColors.PRIME_TIER_COLOR,
    calculatedScoreRange: [580, 700],
  },
  {
    minRawScore: 65,
    tier: BetaScoreTiers.GROWTH_TIER,
    label: BetaScoreLabels.GROWTH_TIER_LABEL,
    colorBucket: BetaScoreColors.GROWTH_TIER_COLOR,
    calculatedScoreRange: [490, 579],
  },
  {
    minRawScore: 50,
    tier: BetaScoreTiers.EMERGING_TIER,
    label: BetaScoreLabels.EMERGING_TIER_LABEL,
    colorBucket: BetaScoreColors.EMERGING_TIER_COLOR,
    calculatedScoreRange: [400, 489],
  },
  {
    minRawScore: 30,
    tier: BetaScoreTiers.MONITOR_TIER,
    label: BetaScoreLabels.MONITOR_TIER_LABEL,
    colorBucket: BetaScoreColors.MONITOR_TIER_COLOR,
    calculatedScoreRange: [280, 399],
  },
  {
    minRawScore: 0,
    tier: BetaScoreTiers.CAUTION_TIER,
    label: BetaScoreLabels.CAUTION_TIER_LABEL,
    colorBucket: BetaScoreColors.CAUTION_TIER_COLOR,
    calculatedScoreRange: [100, 279],
  },
]

export const BetaScoreRatingToDefaultTier = {
  VERY_HIGH: BetaScoreTiers.PRIME_TIER,
  HIGH: BetaScoreTiers.PRIME_TIER,
  MEDIUM_HIGH: BetaScoreTiers.PRIME_TIER,
  MEDIUM: BetaScoreTiers.PRIME_TIER,
  LOW_MEDIUM: BetaScoreTiers.EMERGING_TIER,
  LOW: BetaScoreTiers.EMERGING_TIER,
  VERY_LOW: BetaScoreTiers.MONITOR_TIER,
  POOR: BetaScoreTiers.MONITOR_TIER,
};

export const RiskLevels = {
  LOW: 'Low',
  MEDIUM_LOW: 'Medium-Low',
  MEDIUM: 'Medium',
  MEDIUM_HIGH: 'Medium-High',
  HIGH: 'High',
  UNAVAILABLE: '--',
};

export const DataConfidence = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  UNAVAILABLE: '--',
};

export const TierToRiskLevel = {
  [BetaScoreTiers.PRIME_TIER]: 'Low',
  [BetaScoreTiers.GROWTH_TIER]: 'Medium-Low',
  [BetaScoreTiers.EMERGING_TIER]: 'Medium',
  [BetaScoreTiers.MONITOR_TIER]: 'Medium-High',
  [BetaScoreTiers.CAUTION_TIER]: 'High',
  [BetaScoreTiers.UNAVAILABLE_TIER]: '--',
};

export const TierConfidenceToRiskLevel = {
  [BetaScoreTiers.PRIME_TIER]: {
    [DataConfidence.HIGH]: RiskLevels.LOW,
    [DataConfidence.MEDIUM]: RiskLevels.LOW,
    [DataConfidence.LOW]: RiskLevels.MEDIUM,
  },
  [BetaScoreTiers.GROWTH_TIER]: {
    [DataConfidence.HIGH]: RiskLevels.MEDIUM_LOW,
    [DataConfidence.MEDIUM]: RiskLevels.MEDIUM_LOW,
    [DataConfidence.LOW]: RiskLevels.MEDIUM,
  },
  [BetaScoreTiers.EMERGING_TIER]: {
    [DataConfidence.HIGH]: RiskLevels.MEDIUM,
    [DataConfidence.MEDIUM]: RiskLevels.MEDIUM,
    [DataConfidence.LOW]: RiskLevels.MEDIUM,
  },
  [BetaScoreTiers.MONITOR_TIER]: {
    [DataConfidence.HIGH]: RiskLevels.MEDIUM_HIGH,
    [DataConfidence.MEDIUM]: RiskLevels.MEDIUM_HIGH,
    [DataConfidence.LOW]: RiskLevels.MEDIUM_HIGH,
  },
  [BetaScoreTiers.CAUTION_TIER]: {
    [DataConfidence.HIGH]: RiskLevels.HIGH,
    [DataConfidence.MEDIUM]: RiskLevels.HIGH,
    [DataConfidence.LOW]: RiskLevels.HIGH,
  },
  [BetaScoreTiers.UNAVAILABLE_TIER]: {
    [DataConfidence.HIGH]: RiskLevels.UNAVAILABLE,
    [DataConfidence.MEDIUM]: RiskLevels.UNAVAILABLE,
    [DataConfidence.LOW]: RiskLevels.UNAVAILABLE,
  },
};

export const TierToColorClass = {
  [BetaScoreTiers.PRIME_TIER]: 'tier--prime',
  [BetaScoreTiers.GROWTH_TIER]: 'tier--growth',
  [BetaScoreTiers.EMERGING_TIER]: 'tier--emerging',
  [BetaScoreTiers.MONITOR_TIER]: 'tier--monitor',
  [BetaScoreTiers.CAUTION_TIER]: 'tier--caution',
  [BetaScoreTiers.UNAVAILABLE_TIER]: '',
};

export const BetaScoreDialogs = {
  UNDERSTAND_BETASCORE_HEADER: 'Understand the BetaScore',
  UNDERSTAND_BETASCORE_BODY:
    'The BetaScore summarizes business risk into a tier based on multiple signals and the confidence in those inputs.',

  TIERS_TABLE_HEADER: 'BetaScore Tiers',
  TIERS_TABLE_TITLE: 'Tiers Table',
  TIERS_TABLE_RISK_LABEL: 'Risk Level',
  TIERS_TABLE_DATA_CONFIDENCE_LABEL: 'Data Confidence',
  TIERS_TABLE_BODY_TEMPLATE:
    'Here is how a business is placed in a tier based on the {risk} and {confidence}:',
  TIERS_TABLE_BANNER_TEMPLATE:
    'If the {confidence} is low, the highest tier a business can reach is {emerging}, regardless if the available financials of a business shows “Low Risk.”',
  TIERS_TABLE_EMERGING_TEXT: '“Emerging,”',

  LABEL_TIERS: 'Tiers',
  LABEL_RISK_LEVEL: 'Risk Level',
  LABEL_DATA_CONFIDENCE: 'Data Confidence',

  UB_STEP1_TITLE: 'BetaScore Tiers',
  UB_STEP1_INTRO_TEMPLATE:
    'Businesses are placed in {tiers}, which is BetaScore’s overall grade of a business’ health and readiness to scale. This is determined by assessing their {risk} to our confidence in the available data ({confidence}).',
  UB_TIERS_LIST: [
    { key: 'prime',   title: 'Prime',    desc: 'This business is ready to scale' },
    { key: 'growth',  title: 'Growth',   desc: 'This business has strong profile' },
    { key: 'emerging',title: 'Emerging', desc: 'This is a growing business' },
    { key: 'monitor', title: 'Monitor',  desc: 'This business requires attention' },
    { key: 'caution', title: 'Caution',  desc: 'This business is at risk of failing' }
  ],

  UB_STEP2_TITLE: 'Risk Level',
  UB_RISK_INTRO:
    '{risk} represents the credit risk of a business. BetaScore factors rich financial data from traditional and non-traditional sources for the most accurate assessment of credit risk.',
  UB_RISK_LIST: [
    { key: 'low',     title: 'Low',         desc: 'The business shows proven growth and stability' },
    { key: 'medlow',  title: 'Medium-Low',  desc: 'The business shows promising growth and stability' },
    { key: 'medium',  title: 'Medium',      desc: 'The business shows stability with a short record of growth' },
    { key: 'medhigh', title: 'Medium-High', desc: 'This business has some stability' },
    { key: 'high',    title: 'High',        desc: 'This business has minimal business history or poor financials' }
  ],

  UB_STEP3_TITLE: 'Data Confidence',
  UB_CONFIDENCE_INTRO:
    'To validate the {risk}, BetaScore considers the completeness and accuracy of the information provided on the business.',
  UB_CONFIDENCE_LIST: [
    { key: 'high',   title: 'High',   desc: 'The information provided is complete and up to date.' },
    { key: 'medium', title: 'Medium', desc: 'The information provided is somewhat complete and may be outdated.' },
    { key: 'low',    title: 'Low',    desc: 'The information provided is limited/not enough to verify Risk Level.' }
  ]
}

export const LegalLinks = {
  MSA_URL: 'https://www.betascore.ai/master-service-agreement',
  TERMS_URL: 'https://www.betascore.ai/terms-of-service',
  PRIVACY_URL: 'https://www.betascore.ai/privacy-policy',
}

export const AuthMessageEnum = {
  GENERIC_INSTRUCTIONS:
    "If an account with that email exists, instructions have been sent to verify or reset your access.",
  GENERIC_RESET:
    "If an account with that email exists, a password reset link has been sent. The link will expire in 1 hour.",
  VERIFICATION_SENT: "Verification email sent",
  USER_VERIFIED: "Success user has been verified",
  MISSING_OOBCODE: "Missing oobCode",
  NOT_SIGNED_IN: "Not signed in",
};

export const TrackerScope = {
  YOU: 'you',
  ORGANIZATION: 'organization'
}

export const TrackerSort = {
  RECENT: 'recent',
  OLDEST: 'oldest'
}

export const TrackerQuery = {
  PAGE: 'page',
  SORT: 'sort'
}

export const TrackerHash = {
  YOU: '#you',
  ORGANIZATION: '#organization'
}

export const TrackerTimeState = {
  ACTIVE: 'active',
  HISTORY: 'history',
}

export const ClaimBusinessModal = {
  FOUND : 'FOUND',
  NOT_FOUND : 'NOT_FOUND',
}

export const ClaimBusinessMessage = {
  // Modal
  MODAL_TITLE: "Claim Business",

  // Sections
  YOU_ENTERED: "You entered",
  WE_FOUND: "We found",
  WE_DID_NOT_FIND:
    "We could not find any existing businesses based on the information provided.",

  NOT_FOUND_HINT_1:
    "If your business is new and/or is in the process of being registered with the State, check the box below and continue.",

  NOT_FOUND_HINT_2:
    "If your business does exist, confirm the information is correct and up-to-date. If you continue to experience issues, reach out to support@betascore.ai",

  CONFIRMATION_LABEL:
    "The information is correct and is my business.",

  IRREVERSIBLE_WARNING:
    "You will not be able to modify or change your claimed business. If you do need to make changes AFTER the business has been claimed, reach out to support@betascore.ai",

  BEFORE_CONTINUING:
    "Claiming this business is irreversible. Once confirmed, you will not be able to return to this step or re-claim this business yourself.",

  // Error modal (already-claimed / generic error)
  ERROR_TITLE: "Cannot Claim Business",
  ERROR_BODY:
    "This business has already been claimed and an account created. Contact the primary owner of your business to access the platform.",
  ERROR_FOOTER:
    "If you think this business has been falsely claimed, contact us immediately at support@betascore.ai.",

  // Buttons
  CTA_CLAIM: "Claim Business",
  CTA_GO_BACK: "Go Back",
  CTA_CLOSE: "Close",

  // Form header
  FORM_TITLE: "Provide the following details of your business.",
  FORM_SUBTITLE:
    "If applicable, enter the information registered with Secretary of State.",

  // Helpers
  EMAIL_HELPER:
    "The email associated with your business that is public facing.",
  PHONE_HELPER:
    "The number associated with your business that is public facing.",
};

export const LoanBeaconCopy = {
  TITLE: 'Funding Passport™',
  // OWNER 
  TITLE_ACTIVE: 'Funding Passport™ is',
  DESCRIPTION:
    'Let lenders know you’re looking for a loan. Activate this feature and a badge will be displayed on your profile.',
  DESCRIPTION_ACTIVE: 'Lenders are able view your loan profile and financial documents.',
  DESCRIPTION_INACTIVE: 'Lenders are NOT able view your loan profile and financial documents. You can make edits to your loan profile and financial documents while the Funding Passport™ is not active.',
  CHECKLIST_DESCRIPTION: 'Let lenders know you\'re looking for a loan. Provide helpful financial information to increase your qualification',

  // CHECKLIST
  LOAN_PROFILE_LABEL: 'Loan Profile',
  LOAN_PROFILE_DESCRIPTION: 'Amount, Use, Annual Revenue, Personal Credit Score',
  LOAN_PROFILE_HINT: 'If you\'re unsure, 10% of annual revenue is a good place to start.',
  PERSONAL_CREDIT_SCORE_OWNER_QUESTION: 'Are you the business owner?',
  PERSONAL_CREDIT_SCORE_YES_QUESTION: 'What is your personal credit score?',
  PERSONAL_CREDIT_SCORE_NO_QUESTION: 'What is the personal credit score of the business owner?',
  PERSONAL_CREDIT_SCORE_LATER: 'I\'ll do this later',
  
  // LENDER / BANNER
  BANNER_TITLE: 'This business is seeking a loan',
  BANNER_DESCRIPTION:
    'Contact them directly for more information.',

  FINANCIAL_DOCUMENTS_TITLE: 'Financial Documents',
  FINANCIAL_DOCUMENTS_ACCEPTED_FORMATS_LABEL: 'Accepted Formats:',
  FINANCIAL_DOCUMENTS_ACCEPTED_FORMATS_1: '.pdf, .docx, .xslsx, .csv, .txt',
  FINANCIAL_DOCUMENTS_ACCEPTED_FORMATS_2: '.jpg, .png',
  FINANCIAL_DOCUMENTS_UPLOAD_ERROR: 'Document upload failed',
};

export const LoanBeaconVisual = {
  ICON: {
    SIZE: 48,
    ANIMATION_CANVAS: 72,
  },
};

export const SubscriptionCopy = {
  STATE: {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    TRIAL: 'TRIAL',
    CANCELING: 'CANCELING',
  },

  LABEL: {
    // Pricing cadence
    ANNUAL: 'Annual',
    PER_MONTH: 'Per Month',
    PER_MONTH_PER_SEAT: 'Per Month, Per Seat',
    PULLS_REMAINING: 'Pulls Remaining',
    SEATS: 'Seats',
    TRIAL: '7 Days',

    // Billing section labels
    NEXT_BILLING: 'Next Billing Cycle:',
    TRIAL_ENDS: 'Trial ends',
    LAST_PURCHASE: 'Last Purchase:',
  },

  BUTTON: {
    MANAGE: {
      label: 'Manage',
      event: 'manage',
    },
    MANAGE_SUBSCRIPTION: {
      label: 'Manage Subscription',
      event: 'manage',
    },
    ADD_SEATS_MANAGE: {
      label: 'Add Seats/Manage',
      event: 'manage',
    },
    REACTIVATE: {
      label: 'Reactivate Subscription',
      event: 'reactivate',
    },
    UPGRADE: {
      label: 'Upgrade Subscription',
      event: 'upgrade',
    },
  },

  PLANS: {
    BUSINESS_OWNER: {
      title: 'Business Owner',
      features: [
        'Claim your business',
        'Visible to lenders across the country',
      ],
      hasUpgrade: false,
    },

    TRIAL: {
      title: 'Trial',
      features: [
        'Claim your business',
        'Visible to lenders across the country',
      ],
      hasUpgrade: true,
    },

    PAY_AS_YOU_GO: {
      title: 'Pay as you go',
      features: [
        '10 BetaScore Pulls',
        '$50 per additional pull',
      ],
      hasUpgrade: true,
    },

    STARTER: {
      title: 'Starter',
      features: [
        '35 BetaScore Pulls',
        '$50 per additional pull',
      ],
      hasUpgrade: true,
    },

    CORE: {
      title: 'Core',
      features: [
        '125 BetaScore Pulls',
        '$50 per additional pull',
      ],
      hasUpgrade: true,
    },
  },
};

export const StripeCopy = {
  MANAGE_SUBSCRIPTION: {
    TITLE: "Manage Subscription",

    DESCRIPTION: "Complete checkout to activate your subscription.",

    BACK_BUTTON: "Back to Settings",

    INITIALIZING_CHECKOUT: "Initializing checkout...",

    GENERIC_ERROR:
      "Something went wrong initializing the checkout. Please try again.",

    CHECKOUT_INIT_FAILED:
      "Failed to initialize checkout session.",

    MISSING_PUBLISHABLE_KEY:
      "Missing Stripe publishable key.",

    STRIPE_LOAD_FAILED:
      "Failed to load Stripe.",

    CHECKOUT_CONTAINER_NOT_FOUND:
      "Checkout container not found.",
  },
};

export const SettingsEnum = {
  PAGINATION: {
    PAGE_LIMIT: 10,
  },
  COPY: {
    //Password Copy
    PASSWORD_TITLE: "Password",
    PASSWORD_DESCRIPTION: "Click on the button below to change your password.",
    CHANGE_PASSWORD: "Change Password",
    SAVE_PASSWORD: "Save",
    SAVING_PASSWORD: "Saving...",
    PASSWORD_UPDATED: "Your password has been updated successfully.",
    PASSWORD_ERROR_NO_USER: "No authenticated user found. Please log in again.",
    PASSWORD_ERROR_WRONG_CURRENT: "Current password is incorrect.",
    CURRENT_PASSWORD_LABEL: "Current Password",
    NEW_PASSWORD_LABEL: "New Password",
    CONFIRM_PASSWORD_LABEL: "Confirm New Password",
    CANCEL: "Cancel",

    // Subscription Copy
    SUBSCRIPTION_TITLE: 'Subscription & Billing',
    NO_ACCESS: 'Subscription settings are only available for Business Owners.',
    NO_TRANSACTIONS: 'No transactions found.',
    LOADING_PAYMENTS: 'Loading payment history...',
    DOWNLOAD_CSV: 'Download CSV',
    DOWNLOADING_CSV: 'Downloading...',
    MANAGE_SUBSCRIPTION_TITLE: 'Manage Subscription',
    CANCELLATION_MESSAGE: 'Your subscription has been canceled, but you still have access until',
  },
  CSV: {
    FILENAME: 'billing-history.csv',
    HEADERS: ['Amount', 'Currency', 'Date'],
  },
  HEADER: {
    TITLE: 'User Settings',
    BACK_LABEL: 'Dashboard',
    MANAGE_BACK_LABEL: 'Back to Settings',
  },
};

export const BusinessAttributesCopy = {
  LABEL_ENTITY:            'Entity',
  LABEL_YEARS_IN_BUSINESS: 'Years in business',
  LABEL_COMPANY_SIZE:      'Company size',
  LABEL_ANNUAL_REVENUE:    'Annual revenue',
};